// Converts url hash params:
//   "#foo=foo%20foo&bar=bar%20bar"
// into a js object:
//   { foo: "foo foo", bar: "bar bar" }

import { PlaybackContext } from "enums/PlaybackContext";

export interface QueryMap {
    [key: string]: string | boolean | undefined | null;
}

export function getDecodedQueryParamsFromUri(uri: string): QueryMap {
    const parts = uri.includes("#")
        ? uri.split("#")[1].split("?")
        : uri.split("?");
    const hasQuery = parts.length > 1;
    if (!hasQuery) {
        return {};
    }
    const queryString = parts.pop();
    if (!queryString) {
        return {};
    }
    const queryStringParts = queryString.split("&");
    return queryStringParts
        .map((part: string) => {
            const tmp: QueryMap = {};
            const [key, value] = part.split("=");
            tmp[decodeURIComponent(key)] =
                value === undefined ? value : decodeURIComponent(value);
            return tmp;
        })
        .reduce(
            (acc: QueryMap, curr: QueryMap) => Object.assign(acc, curr),
            {}
        );
}

export function addQueryParamToWindow(queryParams: QueryMap): string {
    return addQueryParams(window.location.hash, queryParams);
}

export function addQueryParams(uri: string, queryParams: QueryMap): string {
    return setQueryParams(uri, {
        ...getDecodedQueryParamsFromUri(uri),
        ...queryParams,
    });
}

export function removeQueryParamFromWindow(queryParam: string): string {
    return removeQueryParam(window.location.hash, queryParam);
}

export function removeQueryParam(uri: string, queryParam: string): string {
    return setQueryParams(uri, {
        ...getDecodedQueryParamsFromUri(uri),
        [queryParam]: null,
    });
}

export function setQueryParams(uri: string, queryParams: QueryMap): string {
    const hashIndex = uri.indexOf("#");
    const queryIndex = uri.indexOf("?", hashIndex);
    let path;
    if (queryIndex !== -1) {
        path = uri.slice(0, queryIndex);
    } else {
        path = uri;
    }
    const queryString = Object.entries(queryParams)
        .filter(([key, value]) => value !== null)
        .map(([key, value]) =>
            value === undefined
                ? encodeURIComponent(key)
                : `${encodeURIComponent(key)}=${encodeURIComponent(
                      value as string
                  )}`
        )
        .join("&");
    return queryString !== "" ? `${path}?${queryString}` : path;
}

export function getDecodedQueryParamsFromWindow(): QueryMap {
    return getDecodedQueryParamsFromUri(window.location.hash);
}

export function getHashRoute(): string {
    return window.location.hash.split("?")[0];
}

export function isFullUrl(location: string) {
    return /^http/.test(location);
}

const newHashContextPattern = /#\/(agent|admin)\/([0-9a-f-]+)\/?.*/i;
const oldHashContextPattern =
    /#\/(engage\/)?(agent|admin)\/(interactions\/)?([0-9a-f-]+)\/?.*/i;

function getHashElement(
    hash: string,
    element: string,
    useNewRoute: boolean
): string {
    // be resilient to window === window.top (we have nowhere to send postMessages to)
    if (useNewRoute) {
        const matches = newHashContextPattern.exec(hash);
        if (matches) {
            if (element === "context" && matches.length > 1) {
                return matches[1];
            } else if (element === "conversationId" && matches.length > 2) {
                return matches[2];
            }
        }
    } else {
        const matches = oldHashContextPattern.exec(
            hash.replace(/#\/engage\//, "#/")
        );
        if (matches) {
            if (element === "context" && matches.length > 2) {
                return matches[2];
            } else if (element === "conversationId" && matches.length > 4) {
                return matches[4];
            }
        }
    }
    return "";
}

/**
 * Defaults to false for backward compatibility with the legacy route format
 * (e.g. #/engage/agent/interactions/<id>). Set to true for the new simplified
 * format (e.g. #/agent/<id>), gated by FeatureToggle.REC_5171_USE_NEW_ANALYTICS_ROUTE.
 */
export function getHashContext(
    hash: string,
    useNewRoute = false
): PlaybackContext {
    const context = getHashElement(
        hash,
        "context",
        useNewRoute
    ) as PlaybackContext;
    if (!context) {
        throw "Couldn't detect a hash context.";
    }
    return context;
}

// Defaults to false for backward compatibility; see getHashContext above for more info.
export function getHashConversationId(
    hash: string,
    useNewRoute = false
): string {
    const conversationId = getHashElement(hash, "conversationId", useNewRoute);
    if (!conversationId) {
        throw "Couldn't detect a hash conversation ID.";
    }
    return conversationId;
}

export function hashify(path: string): string {
    if (path[0] !== "#") {
        return "#" + path;
    } else {
        return path;
    }
}
